import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Suspense } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Invoices } from './pages/Invoices';
import { Employees } from './pages/Employees';
import { NotFound } from './pages/NotFound';
import { Transactions } from './pages/Transactions';
import { Sales } from './pages/Sales';
import { PastSales } from './pages/PastSales';
import { Items } from './pages/Items';
import { Expenses } from './pages/Expenses';

// Protected Owner Route wrapper component using Outlet
function ProtectedOwnerRoute() {
  const { user, role, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner fullscreen />;

  if (!user || role !== 'owner') {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

// Protected Employee Route wrapper component
function ProtectedEmployeeRoute() {
  const { user, role, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner fullscreen />;

  if (!user || role !== 'handler') {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  const { user, role, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullscreen />;
  }

  return (
    <Suspense fallback={<LoadingSpinner fullscreen />}>
      <Routes>
        <Route
          path="/login"
          element={user && role ? <Navigate to={role === 'owner' ? "/dashboard" : "/sales"} replace /> : <Login />}
        />
        
        <Route element={<ProtectedOwnerRoute />}>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="employees" element={<Employees />} />
          </Route>
        </Route>

        <Route element={<ProtectedEmployeeRoute />}>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/sales" replace />} />
            <Route path="sales" element={<Sales />} />
            <Route path="past-sales" element={<PastSales />} />
            <Route path="items" element={<Items />} />
            <Route path="expenses" element={<Expenses />} />
          </Route>
        </Route>
        
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeContextProvider>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeContextProvider>
    </ErrorBoundary>
  );
}

export default App;
